import { useState } from "react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";
import { Button } from "@/components/ui/button";
import { Check, X, ChevronsUpDown } from "lucide-react";

// Searchable, grouped multi-select. Stores + emits an array of LABEL strings
// (matches how the backend profile stores professions/methods/topics).
export function MultiSelectField({ label, hint, options = [], groups = {}, value = [], onChange, max, placeholder, searchPlaceholder = "Suchen…", emptyText = "Nichts gefunden.", testId }) {
  const [open, setOpen] = useState(false);
  const selected = Array.isArray(value) ? value : [];
  const atMax = Boolean(max) && selected.length >= max;

  const toggle = (lbl) => {
    if (selected.includes(lbl)) onChange(selected.filter((x) => x !== lbl));
    else if (!atMax) onChange([...selected, lbl]);
  };
  const remove = (lbl) => onChange(selected.filter((x) => x !== lbl));

  const grouped = {};
  for (const o of options) (grouped[o.group] = grouped[o.group] || []).push(o);
  const groupKeys = Object.keys(grouped);

  return (
    <div>
      <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {label}{max ? ` (max. ${max})` : ""}
      </label>
      {selected.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-1.5">
          {selected.map((lbl, i) => (
            <span key={lbl} data-testid={`${testId}-chip-${i}`} className="inline-flex items-center gap-1 rounded-full bg-primary/10 py-1 pl-2.5 pr-1.5 text-xs font-medium text-primary">
              {lbl}
              <button type="button" onClick={() => remove(lbl)} data-testid={`${testId}-remove-${i}`} className="rounded-full p-0.5 hover:bg-primary/20">
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button type="button" variant="outline" className="w-full justify-between rounded-lg font-normal text-muted-foreground" data-testid={`${testId}-add`}>
            {placeholder || "Hinzufügen…"}
            <ChevronsUpDown className="h-4 w-4 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
          <Command>
            <CommandInput placeholder={searchPlaceholder} data-testid={`${testId}-search`} />
            <CommandList>
              <CommandEmpty>{emptyText}</CommandEmpty>
              {groupKeys.map((gk) => (
                <CommandGroup key={gk} heading={groups?.[gk] || gk}>
                  {grouped[gk].map((o) => {
                    const sel = selected.includes(o.label);
                    return (
                      <CommandItem key={o.key} value={o.label} onSelect={() => toggle(o.label)} disabled={!sel && atMax} data-testid={`${testId}-opt-${o.key}`}>
                        <Check className={`mr-2 h-4 w-4 ${sel ? "opacity-100" : "opacity-0"}`} />
                        {o.label}
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              ))}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}
